import { isObject, stringArray, flattenGrouped } from './helpers';
import { AMENITY_GROUPS } from '../../pages/accommodation/amenities/AmenitiesSection';

// amenity name (lowercase) -> the Amenities step group that has it as a checkbox
// (built on first use, so an import cycle can't leave it empty)
let presetGroups = null;
const presetGroupOf = (lower) => {
    if (!presetGroups) {
        presetGroups = new Map(
            (AMENITY_GROUPS || []).flatMap((group) =>
                (group.items || []).map((item) => [String(item).toLowerCase(), group.key])
            )
        );
    }
    return presetGroups.get(lower);
};

/*
 * Backend category keys (property-level AND unit-level
 * amenities both use this shape).
 */
const BACKEND_AMENITY_KEYS = [
    'essentials',
    'internet',
    'bathroom',
    'bedroom',
    'kitchen',
    'parking',
    'outdoor',
    'entertainment',
    'food',
    'safety',
    'accessibility',
    'services',
    'family',
    'pets'
];

const emptyBackendAmenities = () => {
    const result = {};

    BACKEND_AMENITY_KEYS.forEach((key) => {
        result[key] = [];
    });

    return result;
};

/*
 * Maps each backend category onto the ONE frontend
 * AmenitiesSection group it best matches. Every backend
 * category that has no direct frontend equivalent is folded
 * into "other" so nothing an organizer already saved silently
 * disappears from the edit screen - "other" renders as a
 * freeform, removable tag list, so it's a safe catch-all.
 */
const BACKEND_TO_FRONTEND_GROUP = {
    essentials: 'essential',
    safety: 'safety',
    kitchen: 'kitchen',
    outdoor: 'outdoor',
    entertainment: 'entertainment',

    internet: 'other',
    bathroom: 'other',
    bedroom: 'other',
    parking: 'other',
    food: 'other',
    accessibility: 'other',
    services: 'other',
    family: 'other',
    pets: 'other'
};

/* ============================================================
   PROPERTY AMENITIES - FRONTEND -> BACKEND
============================================================ */

export function transformAmenities(amenities = {}) {
    const result = emptyBackendAmenities();

    if (!isObject(amenities)) {
        return result;
    }

    /*
     * Already categorized in backend shape (e.g. re-submitting
     * data that came from the reverse mapper).
     */
    BACKEND_AMENITY_KEYS.forEach((key) => {
        if (Array.isArray(amenities[key])) {
            result[key] = stringArray(amenities[key]);
        }
    });

    /*
     * Frontend AmenitiesSection shape:
     * { essential: [], safety: [], kitchen: [], outdoor: [],
     *   entertainment: [], other: [] }
     */
    if (Array.isArray(amenities.essential)) {
        result.essentials = stringArray(amenities.essential);
    }

    /*
     * The frontend collects free-typed custom amenities under
     * an "other" bucket that has no matching backend category.
     * Fold them into essentials rather than silently dropping
     * what the organizer typed.
     */
    if (Array.isArray(amenities.other) && amenities.other.length) {
        const customItems = stringArray(amenities.other);

        const existing = new Set(result.essentials.map((item) => item.toLowerCase()));

        customItems.forEach((item) => {
            if (!existing.has(item.toLowerCase())) {
                result.essentials.push(item);
                existing.add(item.toLowerCase());
            }
        });
    }

    return result;
}

/* ============================================================
   PROPERTY AMENITIES - BACKEND -> FRONTEND
============================================================ */

export function reverseAmenities(amenities = {}) {
    const result = {
        essential: [],
        safety: [],
        kitchen: [],
        outdoor: [],
        entertainment: [],
        other: []
    };

    if (!isObject(amenities)) {
        return result;
    }

    const seen = new Set();

    BACKEND_AMENITY_KEYS.forEach((key) => {
        stringArray(amenities[key]).forEach((item) => {
            const lower = item.toLowerCase();
            if (seen.has(lower)) return;
            seen.add(lower);

            // the step group that lists it as a checkbox (e.g. "Laundry" under
            // Other, which is saved with essentials), else by backend group
            const targetGroup = presetGroupOf(lower) || BACKEND_TO_FRONTEND_GROUP[key] || 'other';
            result[targetGroup].push(item);
        });
    });

    return result;
}

/* ============================================================
   UNIT AMENITIES - FRONTEND -> BACKEND
   ------------------------------------------------------------
   The Units step collects a flat list of amenity strings per
   unit. The backend groups unit amenities the same way it
   groups property amenities, so we auto-categorize by keyword.
============================================================ */

export function transformUnitAmenities(amenities = []) {
    const result = emptyBackendAmenities();

    const values = stringArray(flattenGrouped(amenities));

    values.forEach((item) => {
        const lower = item.toLowerCase();

        if (lower.includes('wifi') || lower.includes('wi-fi') || lower.includes('internet')) {
            result.internet.push(item);
        } else if (
            lower.includes('bathroom') ||
            lower.includes('hot water') ||
            lower.includes('shower') ||
            lower.includes('toilet')
        ) {
            result.bathroom.push(item);
        } else if (lower.includes('parking')) {
            result.parking.push(item);
        } else if (lower.includes('tv') || lower.includes('entertainment')) {
            result.entertainment.push(item);
        } else if (lower.includes('kitchen')) {
            result.kitchen.push(item);
        } else if (lower.includes('garden') || lower.includes('balcony') || lower.includes('outdoor')) {
            result.outdoor.push(item);
        } else if (lower.includes('security') || lower.includes('cctv') || lower.includes('fire')) {
            result.safety.push(item);
        } else {
            result.essentials.push(item);
        }
    });

    return result;
}

/* ============================================================
   UNIT AMENITIES - BACKEND -> FRONTEND
   ------------------------------------------------------------
   The Units step just wants one flat array of strings back
   (it renders them as toggle buttons against a fixed preset
   list, matched by exact string).
============================================================ */

export function reverseUnitAmenities(amenities = {}) {
    if (Array.isArray(amenities)) {
        return stringArray(amenities);
    }

    if (!isObject(amenities)) {
        return [];
    }

    const flat = [];
    const seen = new Set();

    BACKEND_AMENITY_KEYS.forEach((key) => {
        stringArray(amenities[key]).forEach((item) => {
            const lower = item.toLowerCase();

            if (!seen.has(lower)) {
                flat.push(item);
                seen.add(lower);
            }
        });
    });

    return flat;
}
